// Copyright ©️ 2025 eVolve MEP, LLC

import type { WorkCell } from '@evolvemep/api-ts';
import { Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { isNotNil } from 'helpers/isNotNil';

type EditableFields = Pick<WorkCell, 'workCellName' | 'workCellDescription'>;

type Props = {
  opened: boolean;
  onClose: () => void;
  workCell?: WorkCell;
  onSubmit: (values: EditableFields) => Promise<void>;
};

const EditWorkCellForm = ({ workCell, onSubmit }: Props) => {
  const form = useForm<EditableFields>({
    initialValues: {
      workCellName: workCell?.workCellName ?? '',
      workCellDescription: workCell?.workCellDescription ?? '',
    },
    validate: {
      workCellName: (v) => !v,
    },
  });

  useFormModal(form, onSubmit);

  return (
    <>
      <TextInput
        label="Work Cell Name"
        required
        autoFocus
        data-autofocus
        {...form.getInputProps('workCellName')}
        maxLength={64}
      />
      <Textarea label="Description" {...form.getInputProps('workCellDescription')} maxLength={255} />
    </>
  );
};

export const CreateEditWorkCellModal = (props: Props) => {
  const { workCell, opened, onClose } = props;
  return (
    <FormModal
      title={`${isNotNil(workCell) ? 'Edit' : 'New'} work cell`}
      confirmationText={isNotNil(workCell) ? 'Save' : 'Create'}
      opened={opened}
      onClose={onClose}
      type="submit"
    >
      <EditWorkCellForm {...props} />
    </FormModal>
  );
};
